import { assetUrl } from "../../lib/api";
import { useLang } from "../../lib/i18n";

const initials = (name = "") => name.split(/\s+/).filter(Boolean).slice(0, 2).map((p) => p[0]).join("").toUpperCase();
const pick = (lang, en, ar) => (lang === "ar" ? ar || en : en || ar);
const SHAPES = { rounded: "rounded-2xl", circle: "rounded-full", square: "rounded-md" };

const rgba = (hex, a) => {
  const n = parseInt(hex.slice(1), 16);
  return `rgba(${n >> 16},${(n >> 8) & 255},${n & 255},${a})`;
};

const withImage = (colors, url, overlay, fade) => {
  if (!url) return colors;
  const o = overlay / 100;
  const shade = fade ? `linear-gradient(180deg, ${rgba(colors.backgroundColor, o)} 0%, ${rgba(colors.backgroundColor, Math.max(o, 0.92))} 60%)` : `linear-gradient(${rgba(colors.backgroundColor, o)}, ${rgba(colors.backgroundColor, o)})`;
  return { ...colors, backgroundImage: `${shade}, url("${assetUrl(url)}")`, backgroundSize: "cover", backgroundPosition: "center top" };
};

const Photo = ({ card, theme, shape, banner }) => {
  const cls = `h-24 w-24 ${SHAPES[shape] || SHAPES.rounded} ${banner ? "ring-4 ring-white" : "ring-2"}`;
  return card.photo_url ? (
    <img src={assetUrl(card.photo_url)} alt={card.name_en} data-testid="card-photo"
      className={`${cls} object-cover`} style={banner ? undefined : { "--tw-ring-color": `${theme.fg}33` }} />
  ) : (
    <div data-testid="card-photo-placeholder" style={{ backgroundColor: banner ? theme.bg : `${theme.fg}1A`, color: theme.fg }}
      className={`${cls} flex items-center justify-center font-display text-2xl font-bold`}>{initials(card.name_en)}</div>
  );
};

export const CardProfile = ({ card, theme, layout = "classic", shape = "rounded", bgImage = "", overlay = 45 }) => {
  const { lang } = useLang();
  const v = Object.fromEntries((card.fields || []).map((f) => [f.key, f.value]));
  const title = pick(lang, card.title_en, card.title_ar);
  const titleIsAr = lang === "ar" && !!card.title_ar;
  const dept = pick(lang, v.department_en, v.department_ar);
  const bio = pick(lang, v.bio_en, v.bio_ar);
  const bioIsAr = lang === "ar" ? !!v.bio_ar : !v.bio_en;
  const banner = layout === "banner";
  const center = layout === "centered" || banner;
  const colors = withImage({ backgroundColor: theme.bg, color: theme.fg }, bgImage, overlay, !banner);
  const arAlign = center ? "text-center" : lang === "ar" ? "text-right" : "text-left";

  return (
    <section className={`relative overflow-hidden rounded-[28px] transition-colors duration-300 ${banner ? "border border-slate-200/80 bg-white text-slate-900" : ""}`}
      style={banner ? undefined : colors} data-testid="card-profile" data-layout={layout} data-has-bg={bgImage ? "true" : "false"}>
      {!banner && <div className="grain pointer-events-none absolute inset-0 opacity-[0.07]" />}
      <div className={`relative px-6 pt-6 ${banner ? "pb-16" : ""}`} style={banner ? colors : undefined}>
        {banner && <div className="grain pointer-events-none absolute inset-0 opacity-[0.07]" />}
        <img src={`/brand/inception-${theme.logo}.png`} alt="Inception" className={`relative h-4 w-auto ${center ? "mx-auto" : ""}`} data-testid="card-logo" />
      </div>
      <div className={`relative flex flex-col px-6 pb-8 ${center ? "items-center text-center" : "items-start"} ${banner ? "-mt-12" : "mt-8"}`}>
        <Photo card={card} theme={theme} shape={shape} banner={banner} />
        <h1 dir="ltr" lang="en" className={`mt-6 w-full font-display text-3xl font-extrabold leading-tight tracking-tight sm:text-4xl ${center ? "text-center" : "text-start"}`} data-testid="card-name-en">
          {card.name_en}
        </h1>
        {card.name_ar && (
          <p dir="rtl" lang="ar" className={`mt-1 w-full font-arabic text-2xl font-medium opacity-85 ${arAlign}`} data-testid="card-name-ar">{card.name_ar}</p>
        )}
        <div className={`mt-4 flex flex-wrap items-center gap-2 ${center ? "justify-center" : ""}`}>
          <span dir={titleIsAr ? "rtl" : "ltr"} style={{ backgroundColor: theme.accent, color: theme.accent_fg }}
            className={`inline-flex rounded-full px-3 py-1 text-sm font-semibold ${titleIsAr ? "font-arabic" : ""}`} data-testid="card-title">{title}</span>
          {dept && <span className="text-sm font-medium opacity-75" data-testid="card-department">{dept}</span>}
        </div>
        {bio && (
          <p dir={bioIsAr ? "rtl" : "ltr"} className={`mt-5 text-sm leading-relaxed opacity-80 ${bioIsAr ? "font-arabic" : ""}`} data-testid="card-bio">{bio}</p>
        )}
      </div>
    </section>
  );
};
